import { useEffect, useRef, useState } from "react";
import { useApp } from "@/hooks/use-app-context";
import { cn } from "@/lib/utils";
import { TaskAudioPlayer } from "./TaskAudioPlayer";
import { useAudioWaveform } from "./use-audio-waveform";
import { useKitCopy } from "./use-kit-copy";

/**
 * A self-contained audio player with the waveform: play/pause button and
 * bars that fill as it plays, click to seek. Use it wherever an audio is
 * played back, instead of the browser's <audio controls>.
 *
 * `<WaveformPlayer src={recordedAudioUrl} />`
 */
export function WaveformPlayer({ src, className, onEnded }: { src: string | null | undefined; className?: string; onEnded?: () => void }) {
  const { theme } = useApp();
  const { copy } = useKitCopy();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { canvasRef } = useAudioWaveform({ audioRef, audioUrl: src, theme });

  useEffect(() => {
    setIsPlaying(false);
  }, [src]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  };

  return (
    <div className={cn(className)}>
      <TaskAudioPlayer
        isPlaying={isPlaying}
        onToggle={toggle}
        canvasRef={canvasRef}
        playLabel={copy.play}
        pauseLabel={copy.pause}
        disabled={!src}
      />
      <audio
        ref={audioRef}
        src={src ?? undefined}
        preload="metadata"
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          onEnded?.();
        }}
      />
    </div>
  );
}
